import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getXtreamServerUrl, useAuth } from './contexts/AuthContext';
import { useDevice } from './contexts/DeviceContext';
import { useFavoritesContext } from './contexts/FavoritesContext';
import { ParentalControlsProvider, useParentalControls } from './contexts/ParentalControlsContext';
import { getDeviceProfiles, getXtreamProxyToken } from './api/backendAPI';
import { Home } from './pages/Home/Home';
import { LiveTVCategories } from './pages/LiveTV/LiveTVCategories';
import { Movies } from './pages/Movies/Movies';
import { Series, SeriesDetailsEntry } from './pages/Series/Series';
import { MovieDetails, MovieDetailsEntry } from './pages/MovieDetails/MovieDetails';
import { SeriesDetails } from './pages/SeriesDetails/SeriesDetails';
import { ContentList, ContentListEntry } from './pages/ContentList/ContentList';
import { Kids } from './pages/Kids/Kids';
import { Anime } from './pages/Anime/Anime';
import { Sports } from './pages/Sports/Sports';
import { ContinueWatching } from './pages/ContinueWatching/ContinueWatching';
import { Boot } from './pages/Boot/Boot';
import Settings from './pages/Settings/Settings';
import { getDefaultPage } from './pages/Settings/sections/InterfaceSection';
import ExitModal from './components/ExitModal/ExitModal';
import ExpiryWarningModal from './components/ExpiryWarningModal/ExpiryWarningModal';
import ScheduleReminderModal from './components/ScheduleReminderModal/ScheduleReminderModal';
import LicenseVerificationModal from './components/LicenseVerificationModal/LicenseVerificationModal';
import { useScheduleNotifier } from './hooks/useScheduleNotifier';
import type { ScheduledProgram } from './hooks/useSchedules';
import { closeApp } from './utils/closeApp';
import { XtreamContent, UICategory } from './api/xtreamAPI';
import xtreamAPI, { XTREAM_HEALTH_EVENT, XtreamHealthEventDetail } from './api/xtreamAPI';
import spinnerGif from './assets/spinner-tv.gif';
import { invalidateLiveStreamsCache } from './utils/liveStreamsCache';
import { invalidateVodStreamsCache } from './utils/vodStreamsCache';
import { invalidateSeriesStreamsCache } from './utils/seriesStreamsCache';
import { invalidateXtreamCategoriesCache } from './utils/xtreamCategoriesCache';
import { clearCatalogCache } from './utils/cacheUtils';
import { catalogHashService } from './services/catalogHashService';
import { CatalogUpdate } from './pages/CatalogUpdate/CatalogUpdate';
import './styles/tokens.css';
import './App.css';


const SURFACE_PROFILE = 'light';

const BASE_WIDTH = 1280;
const BASE_HEIGHT = 720;

type Page = 'home' | 'live-tv-categories' | 'movies' | 'series' | 'kids' | 'anime' | 'sports' | 'movie-details' | 'series-details' | 'content-list' | 'continue-watching' | 'catalog-update';

interface LiveTVEntryState {
  selectedChannel: XtreamContent | null;
  channels: XtreamContent[];
  categories: UICategory[];
  fullscreenRequest?: number;
}

const getViewportScale = () => {
  if (typeof window === 'undefined') return 1;
  const scaleX = window.innerWidth / BASE_WIDTH;
  const scaleY = window.innerHeight / BASE_HEIGHT;
  const scale = Math.min(scaleX, scaleY);
  return Number.isFinite(scale) && scale > 0 ? scale : 1;
};

const AppContent: React.FC = () => {
  const { authData, login, logout } = useAuth();
  const { t } = useTranslation();
  const {
    licenseStatus,
    isDemo,
    deviceId,
    activationPassword,
    licenseVerificationPhase,
    acknowledgeLicenseRecovery,
  } = useDevice();
  const favorites = useFavoritesContext();
  const { isKidsMode } = useParentalControls();
  const [scale, setScale] = useState<number>(() => getViewportScale());
  const [currentPage, setCurrentPage] = useState<Page>(() => {
    if (isKidsMode) return 'kids';
    return getDefaultPage();
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [liveTVEntryState, setLiveTVEntryState] = useState<LiveTVEntryState | null>(null);
  const [movieDetailsEntry, setMovieDetailsEntry] = useState<MovieDetailsEntry | null>(null);
  const [seriesDetailsEntry, setSeriesDetailsEntry] = useState<SeriesDetailsEntry | null>(null);
  const [contentListEntry, setContentListEntry] = useState<ContentListEntry | null>(null);
  const [pageHistory, setPageHistory] = useState<Page[]>([]);
  const [showExitModal, setShowExitModal] = useState(false);
  const [showExpiryWarning, setShowExpiryWarning] = useState(false);
  const licensePausedVideosRef = useRef<HTMLVideoElement[]>([]);
  const [profileError, setProfileError] = useState(false);
  const [noActiveProfile, setNoActiveProfile] = useState(false);
  const [profileRetryCount, setProfileRetryCount] = useState(0);
  const [profileBootReady, setProfileBootReady] = useState(false);
  const [expiryDaysLeft, setExpiryDaysLeft] = useState(0);
  const [reminderSchedule, setReminderSchedule] = useState<ScheduledProgram | null>(null);
  const [xtreamDownVisible, setXtreamDownVisible] = useState(false);

  const handleMovieIdentityResolved = useCallback((tmdbId: number) => {
    setMovieDetailsEntry((current) => current && current.tmdbId !== tmdbId
      ? { ...current, tmdbId }
      : current);
  }, []);

  const handleSeriesIdentityResolved = useCallback((tmdbId: number) => {
    setSeriesDetailsEntry((current) => current && current.tmdbId !== tmdbId
      ? { ...current, tmdbId }
      : current);
  }, []);

  const handleScheduleReminder = useCallback((schedule: ScheduledProgram) => {
    setReminderSchedule(schedule);
  }, []);

  useScheduleNotifier(handleScheduleReminder);

  useEffect(() => {
    if (!deviceId || !activationPassword) {
      xtreamAPI.setProxyTokenProvider(null);
      return;
    }

    xtreamAPI.setProxyTokenProvider(() => getXtreamProxyToken(deviceId, activationPassword));
    return () => xtreamAPI.setProxyTokenProvider(null);
  }, [deviceId, activationPassword]);

  useEffect(() => {
    if (!profileBootReady || !authData) return;
    const endpoint = getXtreamServerUrl(authData.server);
    xtreamAPI.setCredentials(endpoint, authData.user.username, authData.user.password, authData.server.server_protocol);
    catalogHashService.configure(`${endpoint}|${authData.user.username}`);
    void catalogHashService.refreshSequential(false);
  }, [profileBootReady, authData]);

  useEffect(() => {
    let hideTimer: ReturnType<typeof setTimeout> | null = null;
    const onHealth = (event: Event) => {
      const detail = (event as CustomEvent<XtreamHealthEventDetail>).detail;
      if (detail?.status === 'down') {
        setXtreamDownVisible(true);
        if (hideTimer) clearTimeout(hideTimer);
        hideTimer = setTimeout(() => setXtreamDownVisible(false), 15000);
      } else if (detail?.status === 'ok') {
        setXtreamDownVisible(false);
        if (hideTimer) {
          clearTimeout(hideTimer);
          hideTimer = null;
        }
      }
    };
    window.addEventListener(XTREAM_HEALTH_EVENT, onHealth);
    return () => {
      window.removeEventListener(XTREAM_HEALTH_EVENT, onHealth);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, []);

  const handleReminderConfirm = useCallback(() => {
    if (!reminderSchedule) return;
    setReminderSchedule(null);
    const existingChannels = liveTVEntryState?.channels || [];
    const scheduledChannel = existingChannels.find(
      (channel) => String(channel.stream_id || '') === reminderSchedule.streamId
    );
    const channel: XtreamContent = scheduledChannel || {
      stream_id: reminderSchedule.streamId,
      name: reminderSchedule.channelName,
      stream_icon: reminderSchedule.channelIcon,
      category_id: reminderSchedule.categoryId,
      type: 'live',
    };
    setLiveTVEntryState({
      selectedChannel: channel,
      channels: scheduledChannel ? existingChannels : [...existingChannels, channel],
      categories: liveTVEntryState?.categories || [],
      fullscreenRequest: Date.now(),
    });
    setCurrentPage('live-tv-categories');
  }, [reminderSchedule, liveTVEntryState]);

  const navigateTo = (page: Page) => {
    setPageHistory(h => [...h, currentPage]);
    setCurrentPage(page);
  };

  const goBack = (fallback: Page = 'home') => {
    setPageHistory(h => {
      const target = h.length > 0 ? h[h.length - 1] : fallback;
      setCurrentPage(target);
      return h.length > 0 ? h.slice(0, -1) : h;
    });
  };

  const openSearch = () => {
    setContentListEntry({ type: 'movies', source: 'search', title: t('contentList.titleSearch'), instanceId: Date.now() });
    navigateTo('content-list');
  };

  const openFavorites = () => {
    setContentListEntry({ type: 'movies', source: 'favorites', title: t('common.favorites'), instanceId: Date.now() });
    navigateTo('content-list');
  };

  const openHistory = () => {
    navigateTo('continue-watching');
  };

  const openCatalogUpdate = () => {
    setLiveTVEntryState(null);
    navigateTo('catalog-update');
  };

  useEffect(() => {
    if (licenseVerificationPhase !== 'blocked') return;
    const videos = document.querySelectorAll('video');
    const pausedByVerification: HTMLVideoElement[] = [];
    for (let index = 0; index < videos.length; index += 1) {
      if (!videos[index].paused) {
        pausedByVerification.push(videos[index]);
        videos[index].pause();
      }
    }
    licensePausedVideosRef.current = pausedByVerification;
    window.dispatchEvent(new Event('auroraflux:license-verification-blocked'));
  }, [licenseVerificationPhase]);

  const resumeAfterLicenseRecovery = useCallback(() => {
    acknowledgeLicenseRecovery();
    const videos = licensePausedVideosRef.current;
    licensePausedVideosRef.current = [];
    for (let index = 0; index < videos.length; index += 1) {
      const result = videos[index].play();
      if (result && typeof result.catch === 'function') result.catch(() => undefined);
    }
    window.dispatchEvent(new Event('auroraflux:license-verification-resumed'));
  }, [acknowledgeLicenseRecovery]);

  const abandonPlaybackAfterLicenseRecovery = useCallback(() => {
    acknowledgeLicenseRecovery();
    licensePausedVideosRef.current = [];
    window.setTimeout(() => {
      const event = document.createEvent('Event') as KeyboardEvent;
      event.initEvent('keydown', true, true);
      try { Object.defineProperty(event, 'key', { value: 'Escape' }); } catch (_) { /* ignore */ }
      try { Object.defineProperty(event, 'keyCode', { value: 27 }); } catch (_) { /* ignore */ }
      window.dispatchEvent(event);
    }, 0);
  }, [acknowledgeLicenseRecovery]);

  const licenseVerificationModal = licenseVerificationPhase !== 'healthy' ? (
    <LicenseVerificationModal
      phase={licenseVerificationPhase}
      onContinue={resumeAfterLicenseRecovery}
      onBack={abandonPlaybackAfterLicenseRecovery}
    />
  ) : null;

  // ── TV Back Key Normalizer ──────────────────────────────────────────────
  // Samsung Tizen sends keyCode 10009, LG webOS sends 461 — neither sets
  // e.key to 'Escape'. This capture-phase listener intercepts those keycodes
  // and re-dispatches a standard 'Escape' KeyboardEvent so every page handler
  // that checks e.key === 'Escape' / 'GoBack' / 'BrowserBack' works out of the box.
  useEffect(() => {
    const normalizeBackKey = (e: KeyboardEvent) => {
      if ((e as any)._tvNormalized) return;
      const code = (e as any).keyCode || (e as any).which || 0;
      if (code === 10009 || code === 461) {
        e.preventDefault();
        // Patch key in-place so all downstream handlers see 'Escape'.
        // Do NOT stopImmediatePropagation or re-dispatch: the synthetic event
        // loses keyCode (becomes 0), breaking handlers that check keyCode === 10009.
        // Object.defineProperty is Chrome 47-safe — same technique used in polyfills.ts.
        try {
          Object.defineProperty(e, 'key', { value: 'Escape', configurable: true, writable: true });
        } catch (_) {}
        (e as any)._tvNormalized = true;
      }
    };
    window.addEventListener('keydown', normalizeBackKey, true);
    return () => window.removeEventListener('keydown', normalizeBackKey, true);
  }, []);

  // Close Settings automatically when licenseStatus transitions to active
  const prevLicenseStatusRef = useRef(licenseStatus);
  useEffect(() => {
    const prev = prevLicenseStatusRef.current;
    prevLicenseStatusRef.current = licenseStatus;
    if (licenseStatus === 'active' && prev !== 'active' && settingsOpen) {
      setSettingsOpen(false);
    }
  }, [licenseStatus, settingsOpen]);

  // When kids mode is activated (e.g. from Settings), redirect to kids page.
  // Guard: if Settings is open, defer the redirect to when Settings closes,
  // otherwise the Kids page mounts behind the overlay and starts playback/listeners.
  useEffect(() => {
    if (isKidsMode && !settingsOpen) {
      setPageHistory([]);
      setCurrentPage('kids');
    }
  }, [isKidsMode, settingsOpen]);

  // Show expiry warning when account is within 3 days of expiration.
  // Fetches real exp_date from the Xtream API because it is not populated locally.
  useEffect(() => {
    if (!profileBootReady || !authData) return;
    const { server, user } = authData;
    const host = getXtreamServerUrl(server);
    xtreamAPI.setCredentials(host, user.username, user.password, server.server_protocol);
    invalidateLiveStreamsCache();
    invalidateVodStreamsCache();
    invalidateSeriesStreamsCache();
    invalidateXtreamCategoriesCache();
    xtreamAPI.getAccountInfo().then((info) => {
      const expTimestamp = Number(info.exp_date);
      if (!expTimestamp || !Number.isFinite(expTimestamp)) return;
      const msLeft = expTimestamp * 1000 - Date.now();
      const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
      if (daysLeft <= 3) {
        setExpiryDaysLeft(Math.max(daysLeft, 0));
        setShowExpiryWarning(true);
      }
    }).catch(() => undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileBootReady, authData?.user?.username, authData?.server?.url, authData?.server?.port]);

  // Refresh the active profile on every boot. A successful backend response is
  // authoritative for URL/username; an unavailable backend keeps the last valid
  // local profile so playback remains usable offline.
  useEffect(() => {
    if (licenseStatus !== 'active' || !deviceId || !activationPassword) {
      setProfileBootReady(false);
      return;
    }
    setProfileBootReady(false);
    setProfileError(false);
    setNoActiveProfile(false);

    (async () => {
      try {
        const profileRequest = getDeviceProfiles(deviceId, activationPassword);
        const timeout = new Promise<never>((_, reject) => {
          window.setTimeout(() => reject(new Error('Profile refresh timed out')), 10_000);
        });
        const { profiles, activeProfileSelection } = await Promise.race([profileRequest, timeout]);
        if (!profiles.length) { logout(); setNoActiveProfile(true); return; }

        const selectedProfile = activeProfileSelection
          ? profiles.find((p) => p.id === activeProfileSelection.activeProfileId)
          : undefined;
        const activeProfile = selectedProfile?.status === 'active'
          ? selectedProfile
          : profiles.find((profile) => profile.status === 'active');
        if (!activeProfile) { logout(); setNoActiveProfile(true); return; }

        const previousEndpoint = authData ? getXtreamServerUrl(authData.server) : '';
        const nextEndpoint = activeProfile.xtreamUrl.replace(/\/+$/, '');
        const catalogIdentityChanged = Boolean(authData) && (
          authData?.profileId !== activeProfile.id
          || previousEndpoint !== nextEndpoint
          || authData?.user.username !== activeProfile.xtreamUsername
        );
        if (catalogIdentityChanged) await clearCatalogCache();

        // Chrome 47-safe parser; baseUrl retains paths used by some providers.
        const parseProfile = (p: typeof profiles[0]) => {
          let host = p.xtreamUrl;
          let port = '80';
          let protocol = 'http';
          const m = /^(https?):\/\/([^/:]+)(?::(\d+))?/.exec(p.xtreamUrl);
          if (m) {
            protocol = m[1];
            host = m[2];
            port = m[3] || (m[1] === 'https' ? '443' : '80');
          }
          return {
            user: {
              username: p.xtreamUsername,
              password: p.xtreamPassword,
              status: 'Active',
              auth: 1,
              exp_date: '',
              is_trial: '0',
              active_cons: '1',
              created_at: '',
              max_connections: '1',
              allowed_output_formats: ['m3u8', 'ts'] as string[],
            },
            server: {
              url: host,
              port,
              server_protocol: protocol,
              baseUrl: p.xtreamUrl.replace(/\/+$/, ''),
            },
            accessCode: p.resellerCodeSnapshot || '',
            profileDisplayName: p.displayName,
            profileUsesCode: p.requiresResellerCode === true,
            profileId: p.id,
          };
        };

        login(parseProfile(activeProfile));
      } catch {
        if (!authData) setProfileError(true);
      } finally {
        setProfileBootReady(true);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenseStatus, deviceId, activationPassword, profileRetryCount, login, logout]);

  // Auto-poll every 10s when no active profile so the app detects when one is added
  useEffect(() => {
    if (!noActiveProfile) return;
    const timer = setInterval(() => setProfileRetryCount(c => c + 1), 10000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noActiveProfile]);

  useEffect(() => {
    let lastWidth = window.innerWidth;
    let lastHeight = window.innerHeight;

    const handleResize = () => {
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;
      // Ignore resizes where only the height shrank (virtual keyboard opening)
      if (newWidth === lastWidth && newHeight < lastHeight) {
        return;
      }
      lastWidth = newWidth;
      lastHeight = newHeight;
      setScale(getViewportScale());
    };

    // When the virtual keyboard opens the browser scrolls the viewport to reveal
    // the focused input. Snap it back immediately so the app stays in view.
    const handleScroll = () => {
      if (window.scrollX !== 0 || window.scrollY !== 0) {
        window.scrollTo(0, 0);
      }
    };

    setScale(getViewportScale());
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    document.documentElement.dataset.surfaceProfile = SURFACE_PROFILE;
    return () => {
      delete document.documentElement.dataset.surfaceProfile;
    };
  }, []);

  const shellStyle = useMemo(
    () => ({
      transform: `scale(${scale})`,
    }),
    [scale]
  );

  const xtreamDownOverlay = xtreamDownVisible ? (
    <div className="app-xtream-down" role="status" aria-live="polite">
      <img className="app-xtream-down__spinner" src={spinnerGif} alt="" />
      <div className="app-xtream-down__copy">
        <div className="app-xtream-down__title">{t('xtreamStatus.downTitle')}</div>
        <div className="app-xtream-down__body">{t('xtreamStatus.downBody')}</div>
      </div>
    </div>
  ) : null;

  // Show Boot screen until the device is licensed AND a profile is loaded
  const showBoot = licenseStatus !== 'active' || !profileBootReady || !authData;

  if (showBoot) {
    return (
      <div className="app-stage">
        <div className="app-shell" style={shellStyle}>
          <div className="app">
            <Boot
              loadingProfile={licenseStatus === 'active' && !profileBootReady && !profileError && !noActiveProfile}
              profileError={licenseStatus === 'active' && !authData && profileError}
              noActiveProfile={noActiveProfile}
              onRetryProfile={() => { setProfileError(false); setProfileRetryCount(c => c + 1); }}
              onRequestExit={() => setShowExitModal(true)}
            />
            {showExitModal && (
              <ExitModal
                onConfirm={() => closeApp()}
                onCancel={() => setShowExitModal(false)}
              />
            )}
            {xtreamDownOverlay}
            {licenseVerificationModal}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-stage">
      <div className="app-shell" style={shellStyle}>
        <div className="app">
          <>
              {currentPage === 'home' && (
                <Home
                  isDemo={isDemo}
                  paused={settingsOpen}
                  onOpenLiveTV={(entryState) => {
                    // Only update LiveTV entry state when the channel actually changes,
                    // so an already-running stream is not restarted on same-channel navigation.
                    const isSameChannel =
                      liveTVEntryState !== null &&
                      String(entryState.selectedChannel?.stream_id || '') ===
                        String(liveTVEntryState.selectedChannel?.stream_id || '');
                    if (!isSameChannel) {
                      setLiveTVEntryState(entryState);
                    }
                    setCurrentPage('live-tv-categories');
                  }}
                  onPreviewLiveTV={(entryState) => {
                    setLiveTVEntryState((current) => {
                      const currentId = String(current?.selectedChannel?.stream_id || '');
                      const nextId = String(entryState.selectedChannel?.stream_id || '');
                      const sameLists = current?.channels === entryState.channels && current?.categories === entryState.categories;
                      return currentId === nextId && sameLists ? current : entryState;
                    });
                  }}
                  onOpenFavorites={openFavorites}
                  onOpenMovies={() => setCurrentPage('movies')}
                  onOpenSeries={() => setCurrentPage('series')}
                  onOpenKids={() => setCurrentPage('kids')}
                  onOpenAnime={() => setCurrentPage('anime')}
                  onOpenSports={() => setCurrentPage('sports')}
                  onOpenSettings={() => setSettingsOpen(true)}
                  onRequestExit={() => setShowExitModal(true)}
                  onOpenSearch={openSearch}
                  onOpenHistory={openHistory}
                  onUpdateContent={openCatalogUpdate}
                />
              )}

              {currentPage === 'catalog-update' && (
                <CatalogUpdate onClose={() => goBack('home')} />
              )}

              {currentPage === 'movies' && (
                <Movies
                  isActive={currentPage === 'movies' && !settingsOpen}
                  onBack={() => setCurrentPage('home')}
                  onNavigate={(id) => {
                    if (id === 'tv') setCurrentPage('home');
                    if (id === 'series') setCurrentPage('series');
                    if (id === 'kids') setCurrentPage('kids');
                    if (id === 'anime') setCurrentPage('anime');
                    if (id === 'games') setCurrentPage('sports');
                    if (id === 'movies-all') {
                      setContentListEntry({ type: 'movies', title: t('contentList.titleMovies'), instanceId: Date.now() });
                      navigateTo('content-list');
                    }
                    if (id === 'movies-top') {
                      setContentListEntry({ type: 'movies', source: 'top-movies', title: t('contentList.titleMovies'), categoryName: t('movies.top'), instanceId: Date.now() });
                      navigateTo('content-list');
                    }
                    if (id === 'movies-cinema') {
                      setContentListEntry({ type: 'movies', source: 'now-playing', title: t('contentList.titleMovies'), categoryName: t('movies.nowPlaying'), instanceId: Date.now() });
                      navigateTo('content-list');
                    }
                    if (id === 'movies-recent') {
                      setContentListEntry({ type: 'movies', source: 'recent-movies', title: t('contentList.titleMovies'), categoryName: t('movies.releases'), instanceId: Date.now() });
                      navigateTo('content-list');
                    }
                  }}
                  onOpenMovieDetails={(entry) => {
                    setMovieDetailsEntry(entry);
                    navigateTo('movie-details');
                  }}
                  onOpenSettings={() => setSettingsOpen(true)}
                  onOpenSearch={openSearch}
                  onOpenFavorites={openFavorites}
                  onOpenHistory={openHistory}
                />
              )}

              {currentPage === 'series' && (
                <Series
                  isActive={currentPage === 'series' && !settingsOpen}
                  onBack={() => setCurrentPage('home')}
                  onNavigate={(id) => {
                    if (id === 'tv') setCurrentPage('home');
                    if (id === 'movies') setCurrentPage('movies');
                    if (id === 'kids') setCurrentPage('kids');
                    if (id === 'anime') setCurrentPage('anime');
                    if (id === 'games') setCurrentPage('sports');
                    if (id === 'series-all') {
                      setContentListEntry({ type: 'series', title: t('contentList.titleSeries'), instanceId: Date.now() });
                      navigateTo('content-list');
                    }
                    if (id === 'series-top') {
                      setContentListEntry({ type: 'series', source: 'top-series', title: t('contentList.titleSeries'), categoryName: t('series.top'), instanceId: Date.now() });
                      navigateTo('content-list');
                    }
                    if (id === 'series-new') {
                      setContentListEntry({ type: 'series', source: 'on-the-air', title: t('contentList.titleSeries'), categoryName: t('series.new'), instanceId: Date.now() });
                      navigateTo('content-list');
                    }
                    if (id === 'series-national') {
                      setContentListEntry({ type: 'series', source: 'national-series', title: t('contentList.titleSeries'), categoryName: t('series.national'), instanceId: Date.now() });
                      navigateTo('content-list');
                    }
                  }}
                  onOpenSeriesDetails={(entry) => {
                    setSeriesDetailsEntry(entry);
                    navigateTo('series-details');
                  }}
                  onOpenSettings={() => setSettingsOpen(true)}
                  onOpenSearch={openSearch}
                  onOpenFavorites={openFavorites}
                  onOpenHistory={openHistory}
                />
              )}

              {currentPage === 'kids' && (
                <Kids
                  isActive={currentPage === 'kids' && !settingsOpen}
                  onBack={() => isKidsMode ? setShowExitModal(true) : setCurrentPage('home')}
                  onNavigate={(id) => {
                    if (id === 'tv') setCurrentPage('home');
                    if (id === 'movies') setCurrentPage('movies');
                    if (id === 'series') setCurrentPage('series');
                    if (id === 'anime') setCurrentPage('anime');
                    if (id === 'games') setCurrentPage('sports');
                    if (id === 'kids-movies') {
                      setContentListEntry({ type: 'movies', source: 'kids-movies', title: t('contentList.titleKids'), categoryName: t('kids.movies'), instanceId: Date.now() });
                      navigateTo('content-list');
                    }
                    if (id === 'kids-series') {
                      setContentListEntry({ type: 'series', source: 'kids-series', title: t('contentList.titleKids'), categoryName: t('kids.series'), instanceId: Date.now() });
                      navigateTo('content-list');
                    }
                  }}
                  onOpenMovieDetails={(entry) => {
                    setMovieDetailsEntry({ ...entry, isKids: true });
                    navigateTo('movie-details');
                  }}
                  onOpenSeriesDetails={(entry) => {
                    setSeriesDetailsEntry({ ...entry, isKids: true });
                    navigateTo('series-details');
                  }}
                  onOpenSettings={() => setSettingsOpen(true)}
                  onOpenSearch={openSearch}
                  onOpenFavorites={openFavorites}
                  onOpenHistory={openHistory}
                />
              )}

              {currentPage === 'anime' && (
                <Anime
                  isActive={currentPage === 'anime' && !settingsOpen}
                  onBack={() => setCurrentPage('home')}
                  onNavigate={(id) => {
                    if (id === 'tv') setCurrentPage('home');
                    if (id === 'movies') setCurrentPage('movies');
                    if (id === 'series') setCurrentPage('series');
                    if (id === 'kids') setCurrentPage('kids');
                    if (id === 'games') setCurrentPage('sports');
                    if (id === 'anime-series') {
                      setContentListEntry({ type: 'series', source: 'anime-series', title: t('contentList.titleAnime'), categoryName: t('anime.series'), instanceId: Date.now() });
                      navigateTo('content-list');
                    }
                    if (id === 'anime-movies') {
                      setContentListEntry({ type: 'movies', source: 'anime-movies', title: t('contentList.titleAnime'), categoryName: t('anime.movies'), instanceId: Date.now() });
                      navigateTo('content-list');
                    }
                  }}
                  onOpenSeriesDetails={(entry) => {
                    setSeriesDetailsEntry(entry);
                    navigateTo('series-details');
                  }}
                  onOpenMovieDetails={(entry) => {
                    setMovieDetailsEntry(entry);
                    navigateTo('movie-details');
                  }}
                  onOpenSettings={() => setSettingsOpen(true)}
                  onOpenSearch={openSearch}
                  onOpenFavorites={openFavorites}
                  onOpenHistory={openHistory}
                />
              )}

              {currentPage === 'sports' && (
                <Sports
                  isActive={currentPage === 'sports' && !settingsOpen}
                  onBack={() => setCurrentPage('home')}
                  onNavigate={(id) => {
                    if (id === 'tv') setCurrentPage('home');
                    if (id === 'movies') setCurrentPage('movies');
                    if (id === 'series') setCurrentPage('series');
                    if (id === 'kids') setCurrentPage('kids');
                    if (id === 'anime') setCurrentPage('anime');
                  }}
                  onOpenSettings={() => setSettingsOpen(true)}
                  onOpenSearch={openSearch}
                  onOpenFavorites={openFavorites}
                  onOpenHistory={openHistory}
                />
              )}

              {contentListEntry && (
                <div style={currentPage !== 'content-list' ? { display: 'none' } : undefined}>
                  <ContentList
                    entry={contentListEntry}
                    isActive={currentPage === 'content-list' && !settingsOpen}
                    onBack={() => goBack('home')}
                    onOpenMovieDetails={(entry) => {
                      setMovieDetailsEntry(entry);
                      navigateTo('movie-details');
                    }}
                    onOpenSeriesDetails={(entry) => {
                      setSeriesDetailsEntry(entry);
                      navigateTo('series-details');
                    }}
                    onOpenLiveChannel={(ch, allChs) => {
                      setLiveTVEntryState({ selectedChannel: ch, channels: allChs, categories: [] });
                      setCurrentPage('live-tv-categories');
                    }}
                  />
                </div>
              )}

              {currentPage === 'series-details' && seriesDetailsEntry && (
                <SeriesDetails
                  entry={seriesDetailsEntry}
                  isFavorite={favorites.isFavorite(seriesDetailsEntry.seriesStreamId ?? String(seriesDetailsEntry.tmdbId), 'series')}
                  isActive={currentPage === 'series-details' && !settingsOpen}
                  onBack={() => goBack('series')}
                  onToggleFavorite={() => {
                    favorites.toggleFavorite({
                      id: seriesDetailsEntry.seriesStreamId ?? String(seriesDetailsEntry.tmdbId),
                      type: 'series',
                      name: seriesDetailsEntry.title ?? '',
                      icon: seriesDetailsEntry.posterUrl,
                      streamId: seriesDetailsEntry.seriesStreamId,
                      seriesId: seriesDetailsEntry.seriesStreamId,
                      categoryId: seriesDetailsEntry.categoryId,
                      isKids: seriesDetailsEntry.isKids ?? false,
                      updatedAt: Date.now(),
                    });
                  }}
                  onIdentityResolved={handleSeriesIdentityResolved}
                  onPlayEpisode={(episodeStreamId) => {
                    // SeriesDetails handles playback internally
                    console.log('episode started:', episodeStreamId);
                  }}
                  onSelectRecommended={(entry) => {
                    setSeriesDetailsEntry(entry);
                  }}
                />
              )}

              {currentPage === 'continue-watching' && (
                <ContinueWatching
                  isActive={currentPage === 'continue-watching' && !settingsOpen}
                  onBack={() => goBack('home')}
                  onOpenMovieDetails={(entry) => {
                    setMovieDetailsEntry(entry);
                    navigateTo('movie-details');
                  }}
                  onOpenSeriesDetails={(entry) => {
                    setSeriesDetailsEntry(entry);
                    navigateTo('series-details');
                  }}
                />
              )}

              {currentPage === 'movie-details' && movieDetailsEntry && (
                <MovieDetails
                  entry={movieDetailsEntry}
                  isFavorite={favorites.isFavorite(String(movieDetailsEntry.tmdbId || movieDetailsEntry.vodStreamId || ''), 'movies')}
                  isActive={currentPage === 'movie-details' && !settingsOpen}
                  onBack={() => goBack('movies')}
                  onToggleFavorite={(identity) => {
                    favorites.toggleFavorite({
                      id: identity,
                      type: 'movies',
                      name: movieDetailsEntry.title ?? '',
                      icon: movieDetailsEntry.posterUrl,
                      streamId: movieDetailsEntry.vodStreamId,
                      categoryId: movieDetailsEntry.categoryId,
                      isKids: movieDetailsEntry.isKids ?? false,
                      updatedAt: Date.now(),
                    });
                  }}
                  onIdentityResolved={handleMovieIdentityResolved}
                  onFullScreen={(vodStreamId) => {
                    console.log('movie fullscreen:', vodStreamId);
                  }}
                  onSelectRecommended={(entry) => {
                    setMovieDetailsEntry(entry);
                  }}
                />
              )}

              {/* Global Settings overlay — renders on top of any active page */}
              {settingsOpen && (
                <Settings onBack={() => setSettingsOpen(false)} />
              )}

              {/* Exit app confirmation modal */}
              {showExitModal && (
                <ExitModal
                  onConfirm={() => closeApp()}
                  onCancel={() => setShowExitModal(false)}
                />
              )}

              {/* Account expiry warning modal — shown once per session on login when ≤3 days left */}
              {showExpiryWarning && (
                <ExpiryWarningModal
                  daysLeft={expiryDaysLeft}
                  onClose={() => setShowExpiryWarning(false)}
                />
              )}

              {/* Schedule reminder modal — shown ~5 min before a scheduled program starts */}
              {reminderSchedule && (
                <ScheduleReminderModal
                  schedule={reminderSchedule}
                  mode={reminderSchedule.streamId.startsWith('sport_') ? 'info' : 'watch'}
                  onConfirm={handleReminderConfirm}
                  onCancel={() => setReminderSchedule(null)}
                />
              )}

              {/* Uma única instância de vídeo atende Home, listas e fullscreen da Live TV. */}
              {liveTVEntryState && (
                <div className={`app__live-tv-layer${currentPage === 'home' ? ' app__live-tv-layer--home' : ''}${currentPage !== 'home' && currentPage !== 'live-tv-categories' ? ' app__live-tv-layer--hidden' : ''}`}>
                  <LiveTVCategories
                    initialState={liveTVEntryState}
                    isActive={currentPage === 'live-tv-categories' && !settingsOpen}
                    playbackActive={(currentPage === 'home' || currentPage === 'live-tv-categories') && !settingsOpen}
                    homePreview={currentPage === 'home'}
                    onPlayingChannelChange={(entryState) => {
                      setLiveTVEntryState((current) => {
                        const currentId = String(current?.selectedChannel?.stream_id || '');
                        const nextId = String(entryState.selectedChannel?.stream_id || '');
                        return currentId === nextId ? current : entryState;
                      });
                    }}
                    onBack={() => setCurrentPage('home')}
                  />
                </div>
              )}
              {xtreamDownOverlay}
              {licenseVerificationModal}
            </>
        </div>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <ParentalControlsProvider>
      <AppContent />
    </ParentalControlsProvider>
  );
};

export default App;
